import { getAcpAppName } from "@libs/acp/utils/getAcpAppName";
import { identityRefreshToken } from "@libs/acp/apis/signIn/signInAPI";

import {
	generateOneHundredTwentyDays,
	hasValidSession,
	isExpiredToken,
} from "@libs/acp/utils/handleToken";
import { addCookie, removeCookie } from "@libs/utils/helpers/getSafeCookies";
import safestorage from "@libs/utils/helpers/getSafeStorage";

import { isDev } from "@libs/utils/helpers/isDev";
import { isAcOnCpHost } from "@libs/utils/helpers/isAcOnCpHost";
import constants from "../common/constants";
import type { SessionProps } from "./types";

import { useUserStore } from "../store/user/useUserStore";

const isCustomBuild = isAcOnCpHost();

const subjectCookie = isDev()
	? `${constants.legacyAuth.SUBJECT}_DEV`
	: constants.legacyAuth.SUBJECT;

const appName = getAcpAppName();
//TODO: this is basically current AC logout, but outside a custom hook to use in no hook files for a while
export const clearACLocalData = async () => {
	removeCookie(constants.legacyAuth.TOKEN);
	removeCookie(subjectCookie);
	removeCookie(constants.cookies.MFA_POSITIVE_DATA_PASSED);

	// safestorage.removeItem(constants.acAuth.AUTH_USER_TOKEN);

	removeCookie("ATENDE_TOKEN");
	removeCookie("ATENDE_SIGNUP");

	localStorage.removeItem("SORTING_DEBTS_RES");

	const preserveKeys = ["ss_incoming_params", "ss_webReferrer"] as const;
	const preserved = preserveKeys.reduce<Record<string, string | null>>(
		(acc, key) => {
			acc[key] = localStorage.getItem(key);
			return acc;
		},
		{},
	);

	localStorage.clear();
	safestorage.clear().then(() => {
		setTimeout(() => {
			Object.entries(preserved).forEach(([key, value]) => {
				if (value !== null) localStorage.setItem(key, value);
			});
		}, 100);
	});
};

export const shouldRefreshToken = async () => {
	const appAuthMapper = appName === "ac" ? "acAuth" : "cpAuth";

	let authSessionObject = (await safestorage.getItem(
		constants[appAuthMapper].AUTH_USER_TOKEN,
	)) as SessionProps | null;

	if (isCustomBuild || appName === "ac") {
		const authSession = useUserStore.getState().authenticatedUser?.acp?.auth;

		authSessionObject = authSession;
	}

	if (!authSessionObject?.refresh_token) {
		return false;
	}

	const { access_token, refresh_token } = authSessionObject;
	if (hasValidSession({ access_token, refresh_token })) {
		return false;
	}

	if (isExpiredToken(refresh_token)) {
		return false;
	}

	return isExpiredToken(access_token);
};

const applyNewTokens = async (newSession: SessionProps) => {
	addCookie({
		key: constants.legacyAuth.TOKEN,
		value: newSession.access_token,
		expires: generateOneHundredTwentyDays(),
	});

	const authApp = isCustomBuild || appName === "ac" ? "acAuth" : "cpAuth";
	await safestorage.setItem(constants[authApp].AUTH_USER_TOKEN, newSession);

	useUserStore.getState().updateACPAuthSession({
		...useUserStore.getState().authenticatedUser.acp.auth,
		...newSession,
		isAuthenticated: true,
	});
};

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
let refreshPromise: Promise<any> | null = null;

export const handleRefreshToken = async ({
	refreshToken,
}: {
	refreshToken: string;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
}): Promise<any> => {
	if (refreshPromise) {
		return refreshPromise;
	}

	refreshPromise = identityRefreshToken(refreshToken)
		.then(async (response) => {
			const responseData = response?.data?.data;

			const newSession: SessionProps = {
				access_token: responseData.access_token,
				refresh_token: responseData.refresh_token,
			};

			await applyNewTokens(newSession);

			return responseData;
		})
		.catch((err) => {
			// Cleanup and redirect are delegated to the caller so that retry strategies
			// (e.g. attemptRefreshWithRetry) can attempt a second call before giving up.
			console.error("[ERROR_REFRESHING_TOKEN]: ", err);
			throw err;
		})
		.finally(() => {
			refreshPromise = null;
		});

	return refreshPromise;
};
